import { create } from 'zustand';
import { Subrecipe, fetchSubrecipesByRestaurant } from '../services/subrecipes/subrecipesService';

interface SubrecipesStore {
  subrecipesById: Map<string, Subrecipe>;
  subrecipesList: Subrecipe[];
  isLoading: boolean;
  error: string | null;
  currentRestaurantId: string | null;

  load: (restaurantId: string, options?: { force?: boolean; isCurrent?: () => boolean }) => Promise<void>;
  reload: () => Promise<void>;
  byId: (id: string) => Subrecipe | undefined;
  isDataLoadedFor: (restaurantId: string) => boolean;
  clear: () => void;
}

let subrecipeLoadGeneration = 0;
let pendingSubrecipeRestaurantId: string | null = null;

export const useSubrecipesStore = create<SubrecipesStore>((set, get) => ({
  subrecipesById: new Map(),
  subrecipesList: [],
  isLoading: false,
  error: null,
  currentRestaurantId: null,

  load: async (restaurantId: string, options) => {
    if (get().isLoading && pendingSubrecipeRestaurantId === restaurantId && !options?.force) return;
    const generation = ++subrecipeLoadGeneration;
    pendingSubrecipeRestaurantId = restaurantId;
    set({ isLoading: true, error: null });

    try {
      const subrecipes = await fetchSubrecipesByRestaurant(restaurantId);
      if (generation !== subrecipeLoadGeneration) return;
      if (options?.isCurrent && !options.isCurrent()) {
        set({ isLoading: false });
        return;
      }
      const byId = new Map<string, Subrecipe>();
      for (const s of subrecipes) byId.set(s.id, s);

      set({
        subrecipesList: subrecipes,
        subrecipesById: byId,
        currentRestaurantId: restaurantId,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      if (generation !== subrecipeLoadGeneration) return;
      if (options?.isCurrent && !options.isCurrent()) {
        set({ isLoading: false });
        return;
      }
      console.error('Failed to load subrecipes:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load subrecipes',
      });
    }
  },

  reload: async () => {
    const { currentRestaurantId } = get();
    if (currentRestaurantId) await get().load(currentRestaurantId);
  },

  byId: (id: string) => get().subrecipesById.get(id),

  isDataLoadedFor: (restaurantId: string) =>
    get().currentRestaurantId === restaurantId && !get().error,

  clear: () => {
    subrecipeLoadGeneration++;
    pendingSubrecipeRestaurantId = null;
    set({
      subrecipesById: new Map(),
      subrecipesList: [],
      currentRestaurantId: null,
      isLoading: false,
      error: null,
    });
  },
}));
